import { Element, ElementType } from './element';
import { Events } from './events';
import { Model } from './model';
import { Scene } from './scene';
import { Splat } from './splat';

// elements that can be selected and transformed in the scene
type Selectable = Splat | Model;

const registerSelectionEvents = (events: Events, scene: Scene) => {
    // the ordered set of selected elements; `primary` (the last one touched) drives
    // all single-selection behaviour (splat editing, panels, etc.)
    let multi: Selectable[] = [];
    let primary: Selectable = null;

    // all user-facing, selectable scene elements (splats + imported models)
    const getSelectable = () => [
        ...scene.getElementsByType(ElementType.splat),
        ...scene.getElementsByType(ElementType.model)
    ] as Selectable[];

    const fireChange = (prev: Selectable) => {
        events.fire('selection.changed', primary, prev);
        events.fire('multiSelection.changed', multi);
    };

    // replace the selection with a single element (or clear it)
    const setSelection = (element: Selectable) => {
        if (element && !element.visible) {
            return;
        }
        if (element === primary && multi.length <= 1) {
            return;
        }
        const prev = primary;
        multi = element ? [element] : [];
        primary = element ?? null;
        fireChange(prev);
    };

    // add/remove an element from the selection (modifier-click)
    const toggleSelection = (element: Selectable) => {
        if (!element || !element.visible) {
            return;
        }
        const prev = primary;
        const idx = multi.indexOf(element);
        if (idx === -1) {
            multi = [...multi, element];
            primary = element;
        } else {
            multi = multi.filter(e => e !== element);
            primary = multi[multi.length - 1] ?? null;
        }
        fireChange(prev);
    };

    events.on('selection', (element: Selectable) => {
        setSelection(element);
    });

    events.on('selection.toggle', (element: Selectable) => {
        toggleSelection(element);
    });

    events.function('selection', () => {
        return primary;
    });

    events.function('selection.all', () => {
        return multi;
    });

    events.on('selection.next', () => {
        const elements = getSelectable();
        if (elements.length > 1) {
            const idx = elements.indexOf(primary);
            setSelection(elements[(idx + 1) % elements.length]);
        }
    });

    events.on('scene.elementAdded', (element: Element) => {
        if (element.type === ElementType.splat || element.type === ElementType.model) {
            setSelection(element as Selectable);
        }
    });

    events.on('scene.elementRemoved', (element: Element) => {
        if (multi.includes(element as Selectable)) {
            const prev = primary;
            multi = multi.filter(e => e !== element);
            if (primary === element) {
                // fall back to another selected element, then to any remaining splat
                primary = multi[multi.length - 1] ?? null;
                if (!primary) {
                    const elements = getSelectable();
                    primary = elements.find(v => v !== element) ?? null;
                    multi = primary ? [primary] : [];
                }
            }
            fireChange(prev);
        }
    });

    // while true, a visibility change came from timeline playback
    // (object-anim-track.ts), not a direct user hide action - don't
    // deselect for it, or scrubbing into a "hidden" keyframe would keep
    // knocking the animated object out of selection
    let applyingAnimatedVisibility = false;
    events.on('track.applyingAnimatedVisibility', (value: boolean) => {
        applyingAnimatedVisibility = value;
    });

    const onVisibility = (element: Selectable) => {
        if (applyingAnimatedVisibility) {
            return;
        }
        if (multi.includes(element) && !element.visible) {
            const prev = primary;
            multi = multi.filter(e => e !== element);
            if (primary === element) {
                primary = multi[multi.length - 1] ?? null;
            }
            fireChange(prev);
        }
    };
    events.on('splat.visibility', onVisibility);
    events.on('model.visibility', onVisibility);

    events.on('camera.focalPointPicked', (details: { splat: Splat }) => {
        setSelection(details.splat);
    });
};

export { registerSelectionEvents };
